// Select components are injected to avoid circular dependency with @heartplus/ui
// They will be provided by the blocknote-schema factory
import type * as SelectPrimitive from '@radix-ui/react-select';
import type * as React from 'react';

/**
 * The Radix Select component set the dropdown block renders with. Injected at
 * editor-mount time (from `@heartplus/ui`) rather than imported directly, to
 * break the `web-common ↔ ui` circular dependency. Shared with the inline
 * dropdown token renderer via {@link getInjectedSelectComponents}.
 */
export type SelectComponents = {
  Select: typeof SelectPrimitive.Root;
  SelectContent: React.ComponentType<
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
  >;
  SelectItem: React.ComponentType<
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
  >;
  SelectTrigger: React.ComponentType<
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
  >;
  SelectValue: typeof SelectPrimitive.Value;
};

let injectedSelectComponents: SelectComponents | null = null;

/**
 * Inject Select components to be used by dropdown blocks
 * This breaks the circular dependency between web-common and ui
 *
 * Idempotent: calling multiple times with the same components is safe.
 * In development, warns if called with different components (indicates potential bug).
 */
export function injectSelectComponents(components: SelectComponents) {
  if (injectedSelectComponents !== null) {
    // Check if components are the same (reference equality)
    const isSame =
      injectedSelectComponents.Select === components.Select &&
      injectedSelectComponents.SelectContent === components.SelectContent &&
      injectedSelectComponents.SelectItem === components.SelectItem &&
      injectedSelectComponents.SelectTrigger === components.SelectTrigger &&
      injectedSelectComponents.SelectValue === components.SelectValue;
    // If same components, silently return (idempotent)
    if (isSame) {
      return;
    }
  }

  injectedSelectComponents = components;
}

export function getInjectedSelectComponents() {
  return injectedSelectComponents;
}
